import React, { useContext } from 'react'
import { Form } from 'formik';
import { Persist } from '../components/Persist';
import FeatureFlip from '../components/FeatureFlip';

import {
  addDays,
  subDays,
  addHours,
  parseISO,
  isAfter,
  isValid as dateFormatIsValid,
  setHours,
  setMinutes,
  parse,
  getHours,
  getMinutes,
  getDay,
  isSunday,
  isSaturday,
  format,
} from 'date-fns';

import DateFnsUtils from '@date-io/date-fns';
import withWidth, { isWidthUp } from '@material-ui/core/withWidth';

import {
  Typography,
  Grid,
  TextField,
  Button,
  MenuItem,
  Select,
  FormHelperText,
  CircularProgress,
  InputLabel,
  FormControl,
  Box,
  Hidden,
  Checkbox,
  FormControlLabel,
} from '@material-ui/core'

import {
  MuiPickersUtilsProvider,
  KeyboardDatePicker
} from '@material-ui/pickers';

import { KeyboardArrowDown } from '@material-ui/icons';
import ClickableTooltip from '../components/ClickableTooltip';
import InputMask from 'react-input-mask';
import { withStyles } from '@material-ui/core/styles';

import ErrorMessage from '../components/ErrorMessage';
import { generateTimeSlots, isStandard, TIME_WINDOW_STARTS_CONFIG } from '../utils';
import { FeatureFlipContext } from '../context/featureFlipContext'

const products = JSON.parse(document.documentElement.dataset.products);

const SelectWrapper = withStyles(() => ({
  root: {
    width: '100%',
  }
}))(Box);

const SelectProductBox = withStyles(() => ({
  root: {
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    flexBasis: '85%',
  },
}))(Box);

const MaintenanceForm = ({
  values,
  handleChange,
  handleBlur,
  setFieldValue,
  errors,
  touched,
  isValid,
  isLoading,
  errorMessage,
  classes,
  width,
  systemType,
  /* entire "formik bag" available */
}) => {
  const isCoschedulingEnabled = useContext(FeatureFlipContext)['coSchedulingEnabled'];

  const handleProductChange = e => {
    setFieldValue('service.auto_selected_date', true);
    setFieldValue('service.product_sku', e.target.value);
    validateTimeFor(getServiceDate());
  };

  const handleDateChange = (field, value) => {
    if (!value) return;

    setFieldValue('service.auto_selected_date', false);
    setFieldValue('service.date', value);

    validateTimeFor(Date.parse(value));
  };

  const handleTermsChange = () => {
    setFieldValue('terms', !values.terms);
  }

  const getServiceDate = () => {
    const { service } = values;

    return parseISO(service.date);
  }

  const getSelectedDateTime = (hours, minutes = 0) => {
    const serviceDate = getServiceDate();

    if(!dateFormatIsValid(serviceDate)) { return false; }

    return setMinutes(setHours(serviceDate, hours), minutes);
  };

  const isDateBetweenMaxAndMin = (date) => {
    if (!isAfter(date, getMinDate()) || isAfter(date, getMaxDate())) { return false; }

    return availableTimeFramesAfter(date).length > 0
  };

  const isTimeSelectable = (hours, minutes = 0) => {
    const inputDate = getSelectedDateTime(hours, minutes);

    return inputDate && isDateBetweenMaxAndMin(inputDate)
  }

  const isDateTimeAvailable = (dateTime) => {
    return isAvailableTimeFrameExactFor(dateTime) && isDateBetweenMaxAndMin(dateTime)
  }

  const validateTimeFor = (date) => {
    if (!values.service.time || !dateFormatIsValid(date)) return;

    const selectedTime = parse(values.service.time, 'HH:mm', new Date());
    const hours = getHours(selectedTime);
    const minutes = getMinutes(selectedTime);
    const newDateTime = setMinutes(setHours(date, hours), minutes);
    const isTimeValid = isDateTimeAvailable(newDateTime);

    if (isTimeValid) return;

    setFieldValue('service.time', '');
  };

  const availableTimeFramesAfter = (date) => (
    TIME_WINDOW_STARTS_CONFIG[getDay(date)].filter((startHour) => (
      !isAfter(date, setMinutes(setHours(date, startHour), 0))
    ))
  )

  const availableTimeFramesBefore = (date) => (
    TIME_WINDOW_STARTS_CONFIG[getDay(date)].filter((startHour) => (
      isAfter(date, setMinutes(setHours(date, startHour), 0))
    ))
  )

  const isAvailableTimeFrameExactFor = (date) => {
    const hours = getHours(date);
    const timeFrameForExactDateTime = TIME_WINDOW_STARTS_CONFIG[getDay(date)]
      .filter((startHour) => (
        hours === startHour
      ));
    return timeFrameForExactDateTime[0];
  }

  const skipSundayAsMin = (rawMinDate) => {
    let minDate = rawMinDate;
    if (isSunday(minDate)) { return setHours(addDays(minDate, 1), 0); }
    if (isSaturday(minDate) && availableTimeFramesAfter(minDate).length == 0) {
      return setHours(addDays(minDate, 2), 0);
    }
    return minDate;
  }

  const getMinDate = () => {
    const { service } = values;
    // This offset of 3 is to handle a new edge case of "My heating goes out at 12:30PM on a tuesday, I should be able
    // to book an urgent maintenance in the 4PM-8PM window
    const isStandardVisit = isStandard(service.product_sku, systemType);
    const offset = isStandardVisit ? 60 : 3;
    const rawMinDate = addHours(Date.now(), offset);
    let minDate = skipSundayAsMin(rawMinDate);
    let minHours = 0;

    const timeFrames = availableTimeFramesAfter(minDate)

    if (!timeFrames[0]) {
      minDate = addDays(minDate, 1);
    } else {
      // Doing firstTimeFrame - 1 handles the edge case described about from the offset = 3, if just setting to
      // firstTimeFrame then we would not be able to show users the 4PM-8PM window
      minHours = timeFrames[0] - 1;
    }

    minDate = setMinutes(setHours(minDate, minHours), 0);

    if (isStandardVisit) {
      return minDate;
    }

    // BEWARE: very special case
    // Do not allow urgent booking for 8am after 5pm today
    const hoursNow = getHours(Date.now());
    if (hoursNow >= 17 || hoursNow < 9) {
      minDate = setHours(minDate, 11);

      if (isSaturday(minDate)) {
        minDate = setHours(addDays(minDate, 2), 0);
      }
    }

    return minDate;
  };

  const getAutoSelectedDate = () => {
    const { service } = values;

    if(!service.auto_selected_date && service.date) {
      return service.date;
    }

    const currentDate = Date.now();

    let autoSelectedDate;
    if (isStandard(service.product_sku, systemType)) {
      autoSelectedDate = addDays(currentDate, standardTypeOffsetDays())
    } else {
      autoSelectedDate = urgentTypeMaxSelectedDate()
    }
    service.date = format(autoSelectedDate, 'yyyy-MM-dd')

    return service.date;
  }

  const standardTypeOffsetDays = () => {
    const currentDate = Date.now();

    // If it's Wednesday, we preselect Monday instead of Sunday
    return getDay(currentDate) == 3 ? 5 : 4;
  }

  const urgentTypeMaxSelectedDate = () => {
    const currentDate = Date.now();
    // If the time is between noon on Friday and noon on Saturday, we preselect Monday instead of Sunday
    if (
        (getDay(currentDate) == 5 && getHours(currentDate) >= 17) ||
        (getDay(currentDate) == 6)
    ) {
      return addHours(currentDate, 60);
    } else {
      return getMaxDate();
    }
  }

  const getMaxDate = () => {
    const { service } = values;

    // return default maxDate if type is not urgent
    if (isStandard(service.product_sku, systemType)) return undefined;

    const offset = 36;
    let maxDate = addHours(Date.now(), offset);
    let maxHours = maxDate.getHours();

    let timeFrames = availableTimeFramesBefore(maxDate);

    if (!timeFrames[0]) {
      maxDate = subDays(maxDate, 1);
      maxHours = 23;
    }

    maxDate = setMinutes(setHours(maxDate, maxHours), 0);

    return maxDate;
  };

  const anyTimeFramesAvailable = () => {
    return isAfter(getMaxDate(), getMinDate())
  }

  const getWeekDay = (date) => {
    if (!date) { return 0; }

    const selectedDate = parseISO(date);

    return dateFormatIsValid(selectedDate) ? getDay(selectedDate) : 0;
  }

  const customTimeSlots = () => {
    const timeSlots = generateTimeSlots(getWeekDay(values.service.date)).filter(
      time => isTimeSelectable(time.startHour)
    )

    if (timeSlots.length > 0) {
      return timeSlots.map(time =>
        <MenuItem key={time.value} value={time.value}>
          {time.name}
        </MenuItem>
      )
    }
    return (
      <MenuItem disabled>
        No Time Options Available
      </MenuItem>
    )
  }

  const invalidDateMessage = () => {
    return anyTimeFramesAvailable() ? "Date cannot be selected" : "No available time frames";
  };

  return (
    <Form className={`${classes.form} ${classes.maintenanceForm}`}>
      <Typography className={classes.formTitle} variant="h2" align={isWidthUp('sm', width) ? 'center' : 'left'} gutterBottom>
        Enter Your Information
      </Typography>
      <Grid container spacing={3}>
        <Hidden mdDown={true} implementation="js">
          {errorMessage && <ErrorMessage errorMessage={errorMessage} />}
        </Hidden>

        <Grid item xs={12}>
          <TextField
            className={classes.textField}
            name="address.zipcode"
            label="Visit Zip Code / Postal Code"
            fullWidth
            autoComplete="no"
            value={values.address.zipcode}
            onChange={handleChange}
            onBlur={handleBlur}
          />
          {errors.address && errors.address.zipcode && touched.address && touched.address.zipcode ? (
            <FormHelperText error>{errors.address.zipcode}</FormHelperText>
          ) : null}
        </Grid>

        <FeatureFlip
          feature="coSchedulingEnabled"
          on={null}
          off={
            <>
              <Grid className={classes.sectionTitle} item xs={12}>
                <span className={classes.itemDisplayName}>Type of Visit</span>
                <ClickableTooltip tooltip="Select urgent if you want to book for today or tomorrow." />
              </Grid>

              <Grid item xs={12}>
                <FormControl fullWidth>
                  <Select
                    className={classes.select}
                    value={values.service.product_sku}
                    onChange={handleProductChange}
                    onBlur={handleBlur}
                    name="service.product_sku"
                    displayEmpty
                    fullWidth
                    IconComponent={props =>
                      <KeyboardArrowDown
                        {...props}
                      />
                    }
                  >
                    {products != null
                      ? products[systemType].map(product =>
                        <MenuItem key={product.sku} value={product.sku}>
                          <SelectWrapper display="flex" alignItems="center">
                            <SelectProductBox flexGrow={1}>
                              {product.name}
                            </SelectProductBox>
                          </SelectWrapper>
                        </MenuItem>
                      )
                      : ''
                    }
                  </Select>
                  {errors.service && errors.service.product_sku && touched.service && touched.service.product_sku ? (
                    <FormHelperText error>{errors.service.product_sku}</FormHelperText>
                  ) : null}
                </FormControl>
              </Grid>
            </>
            }
          />

        <Grid className={classes.sectionTitle} item xs={12}>
          <span>
            <FeatureFlip feature="coSchedulingEnabled"
                         on={
                           <>
                             Pick a Preferred Date/Time&nbsp;
                             <ClickableTooltip placement="right-end" tooltip="Handy will attempt to send a Pro at the preferred date and time. Otherwise the Pro will call or SMS you to schedule." />
                           </>

                         }
                         off="Pick a Time" />
          </span>
        </Grid>

        <Grid item xs={12} sm={6} md={12} lg={isCoschedulingEnabled ? 6 : 5}>
          <MuiPickersUtilsProvider utils={DateFnsUtils}>
            <KeyboardDatePicker
              className={`${classes.select} ${classes.datePicker}`}
              disableToolbar
              variant="inline"
              format="yyyy-MM-dd"
              margin="none"
              name="service.date"
              label={ <FeatureFlip feature="coSchedulingEnabled"
                                   on="Preferred Date"
                                   off="Select Date" /> }
              value={values.service.date}
              inputValue={getAutoSelectedDate()}
              onChange={handleDateChange}
              onBlur={handleBlur}
              KeyboardButtonProps={{
                'aria-label': 'Change date'
              }}
              fullWidth
              autoOk={true}
              disablePast
              invalidDateMessage=" "
              minDateMessage={invalidDateMessage()}
              maxDateMessage={invalidDateMessage()}
              keyboardIcon={
                <KeyboardArrowDown />
              }
              minDate={getMinDate()}
              maxDate={getMaxDate()}
              shouldDisableDate={(date) => ( getDay(date) === 0 )}
            />
          </MuiPickersUtilsProvider>
          {errors.service && errors.service.date ? (
            <FormHelperText error>{errors.service.date}</FormHelperText>
          ) : null}
        </Grid>

        <Grid item xs={12} sm={6} md={12} lg={isCoschedulingEnabled ? 6 : 7}>
          <FormControl fullWidth>
            <InputLabel>
              <FeatureFlip feature="coSchedulingEnabled"
                           on="Preferred Time"
                           off="Select Time" />
            </InputLabel>
            <Select
              className={classes.select}
              value={values.service.time}
              onChange={handleChange}
              onBlur={handleBlur}
              name="service.time"
              displayEmpty
              fullWidth
              IconComponent={props =>
                <KeyboardArrowDown
                  {...props}
                />
              }
            >
              {
                customTimeSlots()
              }

            </Select>
            {errors.service && errors.service.time && touched.service && touched.service.time ? (
              <FormHelperText error>{errors.service.time}</FormHelperText>
            ) : null}
          </FormControl>
        </Grid>

        <Grid className={classes.sectionTitle} item xs={12}>
          <span>Contact Information</span>
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            className={classes.textField}
            name="user.first_name"
            label="First Name"
            fullWidth
            autoComplete="no"
            value={values.user.first_name}
            onChange={handleChange}
            onBlur={handleBlur}
          />
          {errors.user && errors.user.first_name && touched.user && touched.user.first_name ? (
            <FormHelperText error>{errors.user.first_name}</FormHelperText>
          ) : null}
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            className={classes.textField}
            name="user.last_name"
            label="Last Name"
            fullWidth
            autoComplete="no"
            value={values.user.last_name}
            onChange={handleChange}
            onBlur={handleBlur}
          />
          {errors.user && errors.user.last_name && touched.user && touched.user.last_name ? (
            <FormHelperText error>{errors.user.last_name}</FormHelperText>
          ) : null}
        </Grid>
        <Grid item xs={12}>
          <TextField
            className={classes.textField}
            name="user.email"
            label="Email"
            fullWidth
            autoComplete="no"
            value={values.user.email}
            onChange={handleChange}
            onBlur={handleBlur}
          />
          {errors.user && errors.user.email && touched.user && touched.user.email ? (
            <FormHelperText error>{errors.user.email}</FormHelperText>
          ) : null}
        </Grid>
        <Grid item xs={12}>
          <InputMask
            mask={values.user.phone ? "(999) 999-9999" : ""}
            maskPlaceholder=""
            value={values.user.phone}
            onChange={handleChange}
            onBlur={handleBlur}
          >
            <TextField
              className={classes.textField}
              name="user.phone"
              label="Phone number"
              fullWidth
              autoComplete="no"
            />
          </InputMask>
          {errors.user && errors.user.phone && touched.user && touched.user.phone ? (
            <FormHelperText error>{errors.user.phone}</FormHelperText>
          ) : null}
        </Grid>

        <Grid item xs={12}>
          <Button
            size="large"
            variant="contained"
            color="primary"
            className={classes.button}
            type="submit"
            fullWidth
            disabled={(!isValid || isLoading)}
          >
            {isLoading
              ? <CircularProgress size={35} />
              : 'Schedule'
            }
          </Button>
          <Persist
            isSessionStorage={true}
            ignoreFields={['service.product_sku']}
            name="maintenance-form"
          />
        </Grid>
        <Grid item xs={12}>
          <Hidden lgUp={true} implementation="js">
            {errorMessage && <ErrorMessage errorMessage={errorMessage} />}
          </Hidden>
          <FeatureFlip feature="coSchedulingEnabled"
                       on={
                         <FormControlLabel
                           style={{ alignItems: 'flex-start' }}
                           control={
                             <Checkbox color="primary"
                                       name="terms"
                                       checked={values.terms}
                                       onChange={handleTermsChange}
                             />
                           }
                           label={
                             <Typography className={classes.legalText}>
                               I agree and authorize Handy and its affiliates, and their networks of Service Professionals, to deliver marketing calls or texts using automated technology to the number I provided above regarding this project and other home service offers. Consent is not a condition of purchase.
                             </Typography>
                           }
                         />
                       }
                       off={
                         <Typography className={classes.legalText}>
                           I agree and authorize Handy and its affiliates, and their networks of Service Professionals, to deliver marketing calls or texts using automated technology to the number I provided above regarding this project and other home service offers.  Consent is not a condition of purchase.
                         </Typography>
                       }
          />
        </Grid>
      </Grid>
    </Form>
  );
}
export default withWidth()(MaintenanceForm);
